import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { DashboardLayout } from "@/pages/Dashboard";
import { useLanguage, useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ListingImage } from "@/components/ListingImage";
import AddressMap from "@/components/AddressMap";
import { PROPERTY_TYPES } from "@/lib/listings";
import {
  ArrowRight, MapPin, BedDouble, Bath, Phone, MessageCircle,
  Sofa, Wifi, Zap, User, Calendar, ChevronLeft, ChevronRight, Loader2
} from "lucide-react";
import { toast } from "sonner";
import BackBar from "@/components/BackBar";

export default function PropertyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { user, token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imgIdx, setImgIdx] = useState(0);
  const [showChat, setShowChat] = useState(false);
  const [messageText, setMessageText] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    axios.get(`${API}/properties/${id}`).then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <DashboardLayout title={t("تفاصيل العقار", "Property Detail")}>
        <div className="glass-card rounded-2xl h-96 skeleton"></div>
      </DashboardLayout>
    );
  }
  if (!data) {
    return (
      <DashboardLayout title={t("غير موجود", "Not Found")}>
        <div className="glass-card rounded-2xl p-12 text-center text-slate-400">
          {t("العقار غير موجود", "Property not found")}
        </div>
      </DashboardLayout>
    );
  }

  const typeLbl = PROPERTY_TYPES.find((p) => p.value === data.property_type);
  const images = data.images?.length ? data.images : [null];

  const startChat = async () => {
    if (!user) { navigate("/login"); return; }
    if (!messageText.trim()) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/chat/messages`, {
        recipient_id: data.owner_id,
        content: messageText,
        listing_id: data.property_id,
        listing_type: "property",
        listing_title: data.title
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t("تم إرسال الرسالة", "Message sent"));
      navigate(`/chat/conversation/${res.data.conversation_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || t("فشل الإرسال", "Failed to send"));
    } finally {
      setSending(false);
    }
  };

  return (
    <DashboardLayout title={data.title}>
      <div className="max-w-6xl space-y-6" data-testid="property-detail">
        <BackBar to="/housing" label_ar="سوق العقارات" label_en="Housing Marketplace"
          crumbs={[{ to: "/dashboard", ar: "الرئيسية", en: "Home" }, { to: "/housing", ar: "السكن", en: "Housing" }, { ar: data.title, en: data.title }]} />

        {/* Gallery */}
        <div className="glass-card rounded-2xl overflow-hidden">
          <div className="relative h-96 bg-slate-900">
            <ListingImage path={images[imgIdx]} alt={data.title} className="w-full h-full object-cover" />
            {images.length > 1 && (
              <>
                <button onClick={() => setImgIdx((imgIdx - 1 + images.length) % images.length)}
                  className="absolute top-1/2 right-3 -translate-y-1/2 w-10 h-10 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-yellow-500 hover:text-black transition-colors">
                  <ChevronRight size={20} />
                </button>
                <button onClick={() => setImgIdx((imgIdx + 1) % images.length)}
                  className="absolute top-1/2 left-3 -translate-y-1/2 w-10 h-10 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-yellow-500 hover:text-black transition-colors">
                  <ChevronLeft size={20} />
                </button>
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
                  {images.map((_, i) => (
                    <button key={i} onClick={() => setImgIdx(i)} className={`w-2.5 h-2.5 rounded-full transition-colors ${i === imgIdx ? "bg-yellow-500" : "bg-white/40"}`} />
                  ))}
                </div>
              </>
            )}
          </div>
          {images.length > 1 && (
            <div className="p-3 flex gap-2 overflow-x-auto">
              {images.map((p, i) => (
                <button key={i} onClick={() => setImgIdx(i)} className={`w-20 h-20 rounded-lg overflow-hidden flex-shrink-0 border-2 ${i === imgIdx ? "border-yellow-500" : "border-transparent"}`}>
                  <ListingImage path={p} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main info */}
          <div className="lg:col-span-2 space-y-6">
            <div className="glass-card rounded-2xl p-6">
              <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                  <Badge className="bg-yellow-500/20 text-yellow-400 mb-2">{language === "ar" ? typeLbl?.label_ar : typeLbl?.label_en}</Badge>
                  <h1 className="text-2xl font-bold text-white mb-1">{data.title}</h1>
                  <div className="flex items-center gap-1 text-slate-400">
                    <MapPin size={14} className="text-yellow-500" />
                    <span>{data.city}, {data.state} {data.zip_code}</span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-yellow-400">${data.price?.toLocaleString()}</div>
                  <div className="text-sm text-slate-400">{t("شهرياً", "per month")}</div>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 my-6">
                <div className="bg-slate-900 rounded-xl p-4 text-center">
                  <BedDouble className="text-yellow-500 mx-auto mb-1" size={20} />
                  <div className="text-white font-bold">{data.bedrooms || 0}</div>
                  <div className="text-xs text-slate-400">{t("غرف نوم", "Bedrooms")}</div>
                </div>
                <div className="bg-slate-900 rounded-xl p-4 text-center">
                  <Bath className="text-yellow-500 mx-auto mb-1" size={20} />
                  <div className="text-white font-bold">{data.bathrooms || 0}</div>
                  <div className="text-xs text-slate-400">{t("حمامات", "Bathrooms")}</div>
                </div>
                <div className={`rounded-xl p-4 text-center ${data.furnished ? "bg-yellow-500/10" : "bg-slate-900"}`}>
                  <Sofa className={data.furnished ? "text-yellow-500 mx-auto mb-1" : "text-slate-600 mx-auto mb-1"} size={20} />
                  <div className="text-xs text-slate-400">{data.furnished ? t("مفروش", "Furnished") : t("غير مفروش", "Unfurnished")}</div>
                </div>
                <div className={`rounded-xl p-4 text-center ${data.internet_included ? "bg-yellow-500/10" : "bg-slate-900"}`}>
                  <Wifi className={data.internet_included ? "text-yellow-500 mx-auto mb-1" : "text-slate-600 mx-auto mb-1"} size={20} />
                  <div className="text-xs text-slate-400">{data.internet_included ? t("إنترنت", "Internet") : t("بدون إنترنت", "No internet")}</div>
                </div>
              </div>

              {data.utilities_included && (
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-3 flex items-center gap-2 mb-4">
                  <Zap className="text-yellow-500" size={18} />
                  <span className="text-yellow-400 text-sm font-medium">{t("الفواتير مشمولة في الإيجار", "Utilities included")}</span>
                </div>
              )}

              <div>
                <h3 className="font-bold text-white mb-2">{t("الوصف", "Description")}</h3>
                <p className="text-slate-300 whitespace-pre-wrap leading-relaxed">{data.description}</p>
              </div>
            </div>

            <div className="glass-card rounded-2xl p-6">
              <h3 className="font-bold text-white mb-3 flex items-center gap-2">
                <MapPin className="text-yellow-500" size={18} />
                {t("الموقع على الخريطة", "Location on Map")}
              </h3>
              <AddressMap address={`${data.city}, ${data.state} ${data.zip_code}, USA`} title={data.title} height="h-72" />
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            <div className="glass-card rounded-2xl p-6 space-y-4">
              <h3 className="font-bold text-white">{t("التواصل مع المالك", "Contact Owner")}</h3>
              <div className="flex items-center gap-3 p-3 bg-slate-900 rounded-xl">
                <div className="w-12 h-12 rounded-full bg-yellow-500 text-black flex items-center justify-center font-bold">
                  <User size={20} />
                </div>
                <div>
                  <div className="text-white font-semibold">{data.owner_name}</div>
                  <div className="text-xs text-slate-400 flex items-center gap-1">
                    <Calendar size={12} /> {new Date(data.created_at).toLocaleDateString()}
                  </div>
                </div>
              </div>

              <a href={`tel:${data.phone}`} className="block">
                <Button className="w-full btn-primary" data-testid="call-owner-btn">
                  <Phone className="ml-2" size={18} /> {data.phone}
                </Button>
              </a>

              {user?.user_id !== data.owner_id && (
                <Button onClick={() => setShowChat(!showChat)} variant="outline" className="w-full border-yellow-500/40 text-yellow-400 hover:bg-yellow-500/10" data-testid="open-chat-btn">
                  <MessageCircle className="ml-2" size={18} /> {t("دردشة خاصة", "Private chat")}
                </Button>
              )}

              {showChat && user?.user_id !== data.owner_id && (
                <div className="space-y-2">
                  <textarea
                    className="w-full bg-slate-900 border border-slate-700 rounded-xl px-4 py-3 text-white focus:border-yellow-500 outline-none"
                    rows={3}
                    placeholder={t("اكتب رسالتك...", "Write a message...")}
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                    data-testid="chat-message-input"
                  />
                  <Button disabled={sending || !messageText.trim()} onClick={startChat} className="w-full btn-primary" data-testid="send-chat-btn">
                    {sending ? <Loader2 className="animate-spin" size={16} /> : t("إرسال", "Send")}
                  </Button>
                </div>
              )}
            </div>

            <div className="glass-card rounded-2xl p-5">
              <h4 className="font-bold text-white mb-3">{t("نصائح أمان", "Safety Tips")}</h4>
              <ul className="text-sm text-slate-400 space-y-2 list-disc pr-5">
                <li>{t("عاين العقار شخصياً قبل الدفع", "Inspect in person before paying")}</li>
                <li>{t("لا تحول أموالاً قبل التحقق من المالك", "Don't wire money before verifying")}</li>
                <li>{t("احتفظ بسجل المحادثة هنا", "Keep all chat history here")}</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
